import { Card, DatePicker, Modal, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { FullscreenOutlined, LoadingOutlined } from "@ant-design/icons";
import { disableFutureDates, formatDate } from "../../services/util";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine,
} from "recharts";
dayjs.extend(customParseFormat);

const THRESHOLD = 98.15;
const LINE_COLOR = "#4e9af1";
const THRESHOLD_COLOR = "#fe8447";

function transformChartData(rawData) {
    // rawData: [ [header, "counts"], [label, value], ... ]
    // We skip index 0 (header row)
    return rawData.slice(1).map(([label, value]) => ({
        label: String(label),
        value: Number(value),
        threshold: THRESHOLD,
    }));
}

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div
                style={{
                    background: "#1e2433",
                    border: "1px solid #2e3650",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#e0e6f0",
                }}
            >
                <p style={{ margin: 0, fontWeight: 600, marginBottom: 4 }}>
                    {label}
                </p>
                {payload.map((entry) => (
                    <p
                        key={entry.name}
                        style={{ margin: 0, color: entry.color }}
                    >
                        {entry.name}: {Number(entry.value).toFixed(2)}%
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

function MonthlyChartInner({ chartData, height = 300 }) {
    const data = transformChartData(chartData);
    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart
                data={data}
                margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
            >
                <CartesianGrid strokeDasharray="3 3" stroke="#2a3148" />
                <XAxis
                    dataKey="label"
                    tick={{ fill: "#8a94b0", fontSize: 12 }}
                    axisLine={{ stroke: "#2a3148" }}
                    tickLine={false}
                />
                <YAxis
                    tick={{ fill: "#8a94b0", fontSize: 12 }}
                    axisLine={{ stroke: "#2a3148" }}
                    tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                    wrapperStyle={{
                        color: "#8a94b0",
                        fontSize: 12,
                        paddingTop: 8,
                    }}
                />
                <Line
                    type="monotone"
                    dataKey="value"
                    name="Success Percentage"
                    stroke={LINE_COLOR}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5 }}
                />
                <Line
                    type="monotone"
                    dataKey="threshold"
                    name="Threshold"
                    stroke={THRESHOLD_COLOR}
                    strokeWidth={1.5}
                    dot={false}
                    strokeDasharray="5 5"
                    legendType="none"
                />
            </LineChart>
        </ResponsiveContainer>
    );
}

function MonthlyChart() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [chartData, setChartData] = useState([[`months`, "counts"]]);
    const [enableZoom, setEnableZoom] = useState(false);
    const [formData, setFormData] = useState({
        start_date: formatDate(new Date(new Date().getFullYear(), 0, 1)),
        end_date: formatDate(new Date(new Date().getFullYear(), 11, 31)),
        staff_id,
    });

    const {
        status,
        data: dailySuccessRate,
        refetch,
    } = useQuery(
        "MonthlyManagementChart",
        async () => {
            return await HTTP(`/dashboard/getMonthlySuccessRate`, {
                ...formData,
            });
        },
        { enabled: false },
    );

    useEffect(() => {
        refetch();
    }, [formData]);

    useEffect(() => {
        if (status === "success") {
            setChartData(
                dailySuccessRate.data.map((e, i) => {
                    if (i === 0) return [...e, "Threshold"];
                    else return [...e, THRESHOLD];
                }),
            );
        }
    }, [status, dailySuccessRate]);

    return (
        <Card className="box-shadow">
            <div className="flex space-between">
                <strong>Monthly Success(%)</strong>
                <div className="flex flex-gap-10 align-center">
                    <DatePicker
                        defaultValue={dayjs()}
                        disabledDate={disableFutureDates}
                        picker="year"
                        placeholder="Year"
                        style={{ width: "100px" }}
                        onChange={(_, y) => {
                            setFormData({
                                ...formData,
                                start_date: formatDate(new Date(`${y}`, 0, 1)),
                                end_date: formatDate(new Date(`${y}`, 11, 31)),
                            });
                        }}
                    />
                    <FullscreenOutlined
                        style={{ fontSize: "20px" }}
                        onClick={() => setEnableZoom(true)}
                    />
                </div>
            </div>

            {status === "success" ? (
                <MonthlyChartInner chartData={chartData} height={300} />
            ) : (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "60px 0",
                    }}
                >
                    <Spin
                        indicator={
                            <LoadingOutlined style={{ fontSize: 48 }} spin />
                        }
                    />
                </div>
            )}

            <Modal
                open={enableZoom}
                destroyOnHidden
                onCancel={() => setEnableZoom(false)}
                width="80vw"
                footer={null}
            >
                <div style={{ padding: "16px 0" }}>
                    <strong style={{ fontSize: 16 }}>Monthly Success(%)</strong>
                    <MonthlyChartInner chartData={chartData} height={500} />
                </div>
            </Modal>
        </Card>
    );
}

export default MonthlyChart;
